import {useEffect, useState} from 'react';
import {IModuleTicketFields} from '@shared/services/service-mappers/WorkflowMapper';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useTranslation} from 'react-i18next';
import {allPayersListOfGroup} from '@shared/services/InsuranceEligibilityServices';
import {allHealthCareProvidersOfClinicGroup} from '@shared/services/ClinicServices';
import {TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER} from '@shared/constants/Mapping';
import {getModuleTicketFields} from '@shared/services/WorkflowServices';
import {getAllServiceLocation} from '@shared/services/ServiceLocationService';

interface IUsePriorAuthTicketFieldsData {
  groupId: number | null;
  clinicId: number | null;
}
const usePriorAuthTicketFieldsData = ({
  clinicId,
  groupId
}: IUsePriorAuthTicketFieldsData): {
  clinicPayersList: any[];
  clinicProvidersList: any[];
  serviceLocations: any[];
  customFields: IModuleTicketFields[];
} => {
  const [customFieldOptions, setCustomFieldOptions] = useState<{
    clinicPayersList: any[];
    clinicProvidersList: any[];
    serviceLocations: any[];
  }>({
    clinicPayersList: [],
    clinicProvidersList: [],
    serviceLocations: []
  });
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [customFields, setCustomFields] = useState<IModuleTicketFields[]>([]);

  const clinicGroupHealthCareProviders = async (): Promise<void> => {
    if (!clinicId || !groupId) {
      setCustomFieldOptions(prev => ({
        ...prev,
        clinicProvidersList: []
      }));
      return;
    }
    try {
      const res = await allHealthCareProvidersOfClinicGroup(groupId, '', {
        clinic_id: clinicId
      });
      if (Array.isArray(res?.data?.rows)) {
        setCustomFieldOptions(prev => ({
          ...prev,
          clinicProvidersList: res.data.rows
        }));
      } else {
        setCustomFieldOptions(prev => ({
          ...prev,
          clinicProvidersList: []
        }));
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || t('CLINICS.CLINIC_GROUP_USER_ERROR'),
        SnackBarConfig('e')
      );
    }
  };
  const getPayersListOfGroup = async (): Promise<void> => {
    if (!clinicId || !groupId) {
      setCustomFieldOptions(prev => ({
        ...prev,
        clinicPayersList: []
      }));
      return;
    }
    try {
      const res = await allPayersListOfGroup(groupId);
      if (Array.isArray(res?.data?.rows)) {
        setCustomFieldOptions(prev => ({
          ...prev,
          clinicPayersList: res.data.rows
        }));
      } else {
        setCustomFieldOptions(prev => ({
          ...prev,
          clinicPayersList: []
        }));
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || t('CLINICS.CLINIC_GROUP_USER_ERROR'),
        SnackBarConfig('e')
      );
    }
  };

  const getAllServiceLocations = async (): Promise<void> => {
    if (!groupId) {
      setCustomFieldOptions(prev => ({
        ...prev,
        serviceLocations: []
      }));
      return;
    }
    try {
      const res = await getAllServiceLocation(groupId);
      if (Array.isArray(res?.data?.rows)) {
        setCustomFieldOptions(prev => ({
          ...prev,
          serviceLocations: res.data.rows
        }));
      } else {
        setCustomFieldOptions(prev => ({
          ...prev,
          serviceLocations: []
        }));
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || t('CLINICS.CLINIC_GROUP_USER_ERROR'),
        SnackBarConfig('e')
      );
    }
  };
  useEffect(() => {
    const fetchModuleSpecificTicketFields = async () => {
      if (!groupId) return;
      try {
        const res = await getModuleTicketFields({
          clinic_group_id: groupId,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
                'PRIOR_AUTHORIZATION'
              ]
          }
        });
        if (res.data) {
          setCustomFields(res.data);
        } else {
          setCustomFields([]);
        }
      } catch (error: any) {
        enqueueSnackbar(error?.message, SnackBarConfig('e'));
        setCustomFields([]);
      }
    };
    fetchModuleSpecificTicketFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    getAllServiceLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    clinicGroupHealthCareProviders();
    getPayersListOfGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, clinicId]);

  return {...customFieldOptions, customFields};
};

export default usePriorAuthTicketFieldsData;
